package com.essntl.uikit.components.card

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DisplayMode
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import core.dateformat.utils.formatToFullString
import com.essntl.uikit.components.button.outline.EsMediumOutlineButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import com.essntl.uikit.components.datepicker.EsDatePickerDialog
import com.essntl.uikit.components.datepicker.rememberDatePickerColors
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.tooltip.EsTooltip
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TodayDateCard(
    date: LocalDate,
    onDateSelected: (LocalDate) -> Unit,
) {
    var isDatePickerDialogExpanded by remember {
        mutableStateOf(false)
    }

    Column(
        verticalArrangement = Arrangement.SpaceBetween,
        modifier = Modifier
            .width(264.dp)
            .height(115.dp)
            .background(
                color = MaterialTheme.esColorScheme.tinted,
                shape = RoundedCornerShape(size = 2.spacing),
            )
            .border(
                width = 1.dp,
                color = MaterialTheme.esColorScheme.borderPrimary,
                shape = RoundedCornerShape(size = 2.spacing),
            )
            .padding(1.5.spacing),
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(5.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            EsTooltip(
                text = "Select day",
            ) {
                EsMediumOutlineIconButton(
                    onClick = {
                        isDatePickerDialogExpanded = true
                    },
                    iconRes = EsIcons.Calendar,
                )
            }

            Text(
                text = date.formatToFullString(),
                style = MaterialTheme.esTypography.s2SemiBold16,
            )
        }

        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier = Modifier
                .fillMaxWidth(),
        ) {
            EsMediumOutlineIconButton(
                onClick = {
                    onDateSelected(date.minus(DatePeriod(days = 1)))
                },
                iconRes = EsIcons.ArrowLeft,
            )

            EsMediumOutlineButton(
                onClick = {
                    onDateSelected(Clock.System.now().toLocalDateTime(TimeZone.UTC).date)
                },
                text = "Today",
            )

            EsMediumOutlineIconButton(
                onClick = {
                    onDateSelected(date.plus(DatePeriod(days = 1)))
                },
                iconRes = EsIcons.ArrowRight,
            )
        }
    }

    val datePickerColors = rememberDatePickerColors()

    if (isDatePickerDialogExpanded) {
        val datePickerState = rememberDatePickerState(
            initialSelectedDateMillis =
            try {
                val timeZone = TimeZone.UTC

                date
                    .atStartOfDayIn(timeZone)
                    .toEpochMilliseconds()
            } catch (e: Exception) {
                null
            },
            initialDisplayMode = DisplayMode.Picker,
        )

        EsDatePickerDialog(
            onDismissRequest = {
                isDatePickerDialogExpanded = false
            },
            confirmButton = {
                EsLargePrimaryButton(
                    onClick = {
                        datePickerState.selectedDateMillis?.let { millis ->
                            val localDateTime = Instant
                                .fromEpochMilliseconds(millis)
                                .toLocalDateTime(TimeZone.UTC)

                            val localDate =
                                LocalDate(
                                    year = localDateTime.year,
                                    monthNumber = localDateTime.monthNumber,
                                    dayOfMonth = localDateTime.dayOfMonth,
                                )

                            onDateSelected(localDate)
                        }
                        isDatePickerDialogExpanded = false
                    },
                    text = "Done",
                )
            },
            dismissButton = {
                EsLargeSecondaryButton(
                    onClick = {
                        isDatePickerDialogExpanded = false
                    },
                    text = "Cancel",
                )
            },
            colors = datePickerColors,
            state = datePickerState,
        )
    }
}